import { isAxiosError } from 'axios';

const DEFAULT_MESSAGE = "We couldn't complete your request. Please try again.";
const VALIDATION_MESSAGE = 'Please check the information you entered and try again.';

/** Read display text only; structured error objects must never reach a toast. */
function responseMessage(body: unknown): string | undefined {
  if (!body || typeof body !== 'object') return undefined;
  const record = body as Record<string, unknown>;
  for (const key of ['message', 'detail', 'error']) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  for (const key of ['message', 'detail', 'error']) {
    const value = record[key];
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const nested = value as Record<string, unknown>;
      if (typeof nested['message'] === 'string' && nested['message'].trim()) {
        return nested['message'].trim();
      }
    }
  }
  return undefined;
}

/** Backend business messages take precedence for client errors only. */
export function toMessage(error: unknown, fallback = DEFAULT_MESSAGE): string {
  if (!isAxiosError(error)) return fallback;
  const status = error.response?.status;
  if (status === undefined) {
    if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
      return 'The request took too long. Please try again.';
    }
    return 'Unable to connect. Please check your internet connection and try again.';
  }
  // Infrastructure failures may contain proxy pages or exception details.
  if (status >= 500) return fallback;
  const message = responseMessage(error.response?.data);
  if (message) return message;
  switch (status) {
    case 401:
      return 'Your session has expired. Please sign in again.';
    case 403:
      return 'You do not have permission to perform this action.';
    case 404:
      return 'This item could not be found. Please refresh the page and try again.';
    case 409:
      return 'This item has changed or already exists. Please refresh the page and try again.';
    case 413:
      return 'The file is too large. Please choose a smaller file.';
    case 400:
    case 422:
      return VALIDATION_MESSAGE;
    case 429:
      return 'Too many requests. Please wait a moment and try again.';
    default:
      return fallback;
  }
}

/** Downloads follow the same message rules after decoding the JSON error body. */
export async function blobErrorToMessage(error: unknown, fallback: string): Promise<string> {
  if (isAxiosError(error) && error.response?.data instanceof Blob) {
    try {
      const parsed: unknown = JSON.parse(await error.response.data.text());
      return toMessage(
        { ...error, isAxiosError: true, response: { ...error.response, data: parsed } },
        fallback,
      );
    } catch {
      return fallback;
    }
  }
  return toMessage(error, fallback);
}
